import { FC, useEffect, useState } from 'react';
import { useRouter } from 'next/router';

import {
    AlertDisabledMedium,
    AlertMedium,
} from '@zoopla-eng/zds-components/icons';
import { IconButton } from '@zoopla-eng/zds-components/button';

import { ConsentModal, ConsentRevokeModal } from '@zoopla/consent-modal';
import { useTrackPropertyContext } from '@zoopla/property-details/track-property';
import {
    EventNames,
    EventNamesList,
    trackSegmentAndGAEvent,
} from '@zoopla/tracking';

type TrackingData = {
    eventName: EventNamesList;
    eventAction: string;
    eventLabel: string;
    path: string;
};

type ConsentActionProps = {
    uprn: string;
};

const PROPERTY_PATH_PREFIX = '/property/uprn/';
const MY_HOME_PATH_PREFIX = '/myaccount/my-home/uprn/';

const ConsentAction: FC<ConsentActionProps> = ({ uprn }) => {
    const {
        dispatch,
        state: { relationship, hasPermissions },
    } = useTrackPropertyContext();

    const router = useRouter();
    const [path] = router.asPath.split('?');
    const [isConsentModalShowing, setIsConsentModalShowing] = useState(false);
    const [isConsentButtonLoading, setIsConsentButtonLoading] = useState(false);

    useEffect(() => {
        setIsConsentButtonLoading(false);
    }, [hasPermissions]);

    const hostPage = router.asPath.includes(PROPERTY_PATH_PREFIX)
        ? 'Property Details Page'
        : 'Hy Home Estimate';

    const consentModalBaseProps = {
        closeModal: () => setIsConsentModalShowing(false),
        isOpen: isConsentModalShowing,
        uprn,
        hostPage,
    };

    const trackingHandler = ({
        eventName,
        eventAction,
        eventLabel,
        path,
    }: TrackingData) => {
        if (
            path.includes(PROPERTY_PATH_PREFIX) ||
            path.includes(MY_HOME_PATH_PREFIX)
        ) {
            const eventCategory = path.includes(PROPERTY_PATH_PREFIX)
                ? 'property details'
                : 'my home estimate';

            trackSegmentAndGAEvent(
                eventName,
                {
                    eventCategory,
                    eventAction,
                    eventLabel,
                    eventValue: uprn || undefined,
                },
                path
            );
        }
    };

    const revokeConsentClickHandler = () => {
        trackingHandler({
            eventName: EventNames.ALERTS_CONSENT_OFF,
            eventAction: 'click',
            eventLabel: 'property consent off',
            path,
        });
    };

    const revokeDismissModalHandler = () => {
        trackingHandler({
            eventName: EventNames.MODAL_DISMISSED,
            eventAction: 'dismiss modal',
            eventLabel: 'property consent off',
            path,
        });
    };

    const consentClickHandler = () => {
        trackingHandler({
            eventName: EventNames.ALERTS_CONSENT_ON,
            eventAction: 'click',
            eventLabel: 'property consent on',
            path,
        });
    };

    const dismissModalHandler = () => {
        trackingHandler({
            eventName: EventNames.MODAL_DISMISSED,
            eventAction: 'dismiss modal',
            eventLabel: 'property consent on',
            path,
        });
    };

    const handleAlertOnClick = () => {
        if (!isConsentButtonLoading) {
            setIsConsentModalShowing(true);
            trackingHandler({
                eventName: EventNames.MODAL_SHOWN,
                eventAction: 'modal shown',
                eventLabel: `property consent ${hasPermissions ? 'off' : 'on'}`,
                path,
            });
        }
    };

    return (
        <>
            {hasPermissions ? (
                <ConsentRevokeModal
                    {...consentModalBaseProps}
                    updateStatus={(_, result) => {
                        if (result === 'untracked') {
                            dispatch({ type: 'revokeConsent' });
                        }
                        if (result === 'pending') {
                            setIsConsentButtonLoading(true);
                        }
                    }}
                    handleConsentClick={revokeConsentClickHandler}
                    handleDismissClick={revokeDismissModalHandler}
                />
            ) : (
                <ConsentModal
                    {...consentModalBaseProps}
                    relationship={relationship || 'NONE'}
                    updateStatus={(_, result) => {
                        if (result === 'tracked') {
                            dispatch({ type: 'allowConsent' });
                        }
                        if (result === 'pending') {
                            setIsConsentButtonLoading(true);
                        }
                    }}
                    handleConsentClick={consentClickHandler}
                    handleDismissClick={dismissModalHandler}
                />
            )}
            <IconButton
                data-testid="consent-action-button"
                label={`Turn marketing consent ${
                    hasPermissions ? 'off' : 'on'
                }`}
                category="secondary"
                onClick={handleAlertOnClick}
                disabled={isConsentButtonLoading}
            >
                {!hasPermissions ? AlertDisabledMedium : AlertMedium}
            </IconButton>
        </>
    );
};

export default ConsentAction;
